'use client';

import { type RefObject, useLayoutEffect, useRef, useState } from 'react';

import { useDevice } from '@/contexts/DeviceContext';

type UseCarouselReturn = {
  chevLeft: boolean;
  chevRight: boolean;
  containerRef: RefObject<HTMLUListElement | null>;
  scrollLeft: () => void;
  scrollRight: () => void;
  isReady: boolean;
};

export const useCarousel = (): UseCarouselReturn => {
  const [chevLeft, setChevLeft] = useState<boolean>(true);
  const [chevRight, setChevRight] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);

  const containerRef = useRef<HTMLUListElement>(null);

  const { isMobile } = useDevice();

  const scrollLeft = () => {
    if (isMobile || !containerRef.current) return;

    const container = containerRef.current;

    container.scrollTo({
      left: container.scrollLeft - container.clientWidth,
      behavior: 'smooth',
    });
  };

  const scrollRight = () => {
    if (isMobile || !containerRef.current) return;

    const container = containerRef.current;

    container.scrollTo({
      left: container.scrollLeft + container.clientWidth,
      behavior: 'smooth',
    });
  };

  useLayoutEffect(() => {
    if (isMobile || !containerRef.current) return;

    const container = containerRef.current;
    const onScroll = () => {
      const tolerance = 5;
      setChevLeft(container.scrollLeft <= tolerance);
      setChevRight(
        container.scrollLeft + container.clientWidth >= container.scrollWidth - tolerance,
      );
      if (!isReady) {
        setIsReady(true);
      }
    };

    onScroll();
    container.addEventListener('scroll', onScroll);

    return () => container.removeEventListener('scroll', onScroll);
  }, [isMobile, isReady]);

  return {
    chevLeft,
    chevRight,
    containerRef,
    scrollLeft,
    scrollRight,
    isReady,
  };
};
